import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  AnalyticsDestination,
  CreateAnalyticsDestinationRequest,
  PatchAdminV1StoresStoreIdAnalyticsDestinationsIdBody,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';

import { analyticsApi } from '../api/analytics-api';

import { analyticsKeys } from './analytics-keys';

/**
 * 409 ALREADY_EXISTS on `provider` when someone connected it meanwhile; the list is refetched so
 * the existing destination shows up behind the dialog.
 */
export function useCreateAnalyticsDestination(storeId: string) {
  const queryClient = useQueryClient();
  const key = analyticsKeys.destinations(storeId);
  return useMutation({
    mutationFn: (body: CreateAnalyticsDestinationRequest) => analyticsApi.create(storeId, body),
    meta: { successMessage: 'analytics:toasts.created', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (error) => {
      if (isApiError(error) && error.kind === 'conflict') {
        return queryClient.invalidateQueries({ queryKey: key });
      }
      return undefined;
    },
  });
}

/**
 * Pausing and resuming (a body of `status` alone) is the one optimistic update: an idempotent
 * boolean the switch would otherwise lag behind. Config and credential changes wait. A null
 * `successMessage` leaves the toast to the caller.
 */
export function useUpdateAnalyticsDestination(
  storeId: string,
  successMessage: string | null = 'analytics:toasts.updated',
) {
  const queryClient = useQueryClient();
  const key = analyticsKeys.destinations(storeId);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdAnalyticsDestinationsIdBody;
    }) => analyticsApi.update(storeId, id, body),
    meta: successMessage ? { successMessage, silentError: true } : { silentError: true },
    onMutate: async ({ id, body }) => {
      const { status } = body;
      if (status === undefined || Object.keys(body).length !== 1) return undefined;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AnalyticsDestination[]>(key);
      queryClient.setQueryData<AnalyticsDestination[]>(key, (rows) =>
        rows?.map((row) => (row.id === id ? { ...row, status } : row)),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteAnalyticsDestination(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analyticsApi.remove(storeId, id),
    meta: { successMessage: 'analytics:toasts.deleted', silentError: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: analyticsKeys.destinations(storeId) }),
  });
}

/**
 * Sends a test page_view and reports the platform's answer; a refusal by the platform is a 200
 * with `ok: false`, so the result renders inline instead of a toast.
 */
export function useTestAnalyticsDestination(storeId: string) {
  return useMutation({
    mutationFn: (id: string) => analyticsApi.test(storeId, id),
    meta: { silentError: true },
  });
}
